import { useCallback, useEffect, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import moment from "moment";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { ImageUpload } from "@/components/ImageUpload";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import FormattedNumberInput from "@/components/FormattedNumberInput";

// Icons
import { PenLine, Plus, Save, Trash2, Users } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

// Services
import { getSOE } from "@/services/soe/general";
import { updateSOE } from "@/services/soe";

// Types
import {
  getProvinces,
  getCoreSectors as getSectors,
  getDistrict,
} from "@/services/master";
import type { Provinces, CoreSector, Districts } from "@/types/master";
import { Spinner } from "@/components/ui/spinner";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";

export const Route = createFileRoute(
  "/_layout-private/account-management/soe/_information/general/"
)({
  component: GeneralInformation,
});

// Mock data for government and private companies
const mockGovCompanies = [
  { id: "gov1", name: "ກະຊວງປ້ອງກັນປະເທດ" },
  { id: "gov2", name: "ກະຊວງປ້ອງກັນຄວາມສະຫງົບ" },
  { id: "gov3", name: "ກະຊວງການຕ່າງປະເທດ" },
  { id: "gov4", name: "ກະຊວງຍຸຕິທຳ" },
  { id: "gov5", name: "ກະຊວງອຸດສາຫະກຳ ແລະ ການຄ້າ" },
  { id: "gov6", name: "ກະຊວງການເງິນ" },
  { id: "gov7", name: "ກະຊວງກະສິກຳ ແລະ ສິ່ງແວດລ້ອມ" },
  { id: "gov8", name: "ກະຊວງໂຍທາທິການ ແລະ ຂົນສົ່ງ" },
  { id: "gov9", name: "ກະຊວງສຶກສາທິການ ແລະ ກິລາ" },
  { id: "gov10", name: "ກະຊວງສາທາລະນະສຸກ" },
  { id: "gov11", name: "ກະຊວງແຮງງານ ແລະ ສະຫວັດດີການສັງຄົມ" },
  { id: "gov12", name: "ກະຊວງວັດທະນະທຳ ແລະ ທ່ອງທ່ຽວ" },
  { id: "gov13", name: "ກະຊວງເຕັກໂນໂລຊີ ແລະ ການສື່ສານ" },
];

// Type for domestic shareholder
interface DomesticShareholder {
  id: string;
  percentage: number;
  companyName: string;
}

// Type for shareholding data from backend
interface ShareholdingData {
  id: string;
  companyId: string;
  govShareholding: number | null;
  govName: string | null;
  privateShareholding: number | null;
  privateName: string | null;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// Component for dynamic domestic shareholders using useFieldArray
function DomesticShareholdersFieldArray({
  onDomShareholdersChange,
  initialShareholders,
}: {
  govShareholding: number;
  onGovChange: (value: number) => void;
  govCompanyName: string;
  onGovCompanyChange: (value: string) => void;
  onDomShareholdersChange: (shareholders: DomesticShareholder[]) => void;
  initialShareholders?: DomesticShareholder[];
}) {
  const { control, watch, reset } = useForm<{
    shareholders: DomesticShareholder[];
  }>({
    defaultValues: {
      shareholders:
        initialShareholders && initialShareholders.length > 0
          ? initialShareholders
          : [{ id: "1", percentage: 0, companyName: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "shareholders",
  });

  const shareholders = watch("shareholders");

  // Reset form when initialShareholders changes (when modal opens with backend data)
  useEffect(() => {
    if (initialShareholders && initialShareholders.length > 0) {
      reset({
        shareholders: initialShareholders,
      });
    }
  }, [initialShareholders, reset]);

  // Calculate totals and notify parent
  useEffect(() => {
    console.log("Shareholders array changed:", shareholders);
    if (shareholders && shareholders.length > 0) {
      // Create a deep copy to ensure we have fresh data
      const shareholdersCopy = shareholders.map((sh) => ({
        id: sh.id,
        percentage: Number(sh.percentage) || 0,
        companyName: sh.companyName || "",
      }));
      console.log("Shareholders copy with parsed numbers:", shareholdersCopy);
      onDomShareholdersChange(shareholdersCopy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(shareholders)]);

  return (
    <div className="space-y-4">
      {/* Domestic Shareholders - Dynamic Fields */}
      <div className="space-y-3">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="p-3 border rounded-lg space-y-2 bg-gray-50"
          >
            <div className="flex items-center justify-end mb-2">
              {fields.length > 1 && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(index)}
                  className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <Label htmlFor={`shareholders.${index}.percentage`}>
                  ເອກະຊົນຖືຮຸ້ນ (%) <span className="text-red-500">*</span>
                </Label>
                <Controller
                  control={control}
                  name={`shareholders.${index}.percentage`}
                  render={({ field: controllerField }) => {
                    const hasError =
                      controllerField.value > 0 &&
                      (!shareholders[index]?.companyName ||
                        shareholders[index]?.companyName.trim() === "");
                    return (
                      <Input
                        {...controllerField}
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="0.00"
                        value={controllerField.value || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          controllerField.onChange(
                            value === "" ? 0 : Number(value)
                          );
                        }}
                        className={hasError ? "border-red-500" : ""}
                      />
                    );
                  }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor={`shareholders.${index}.companyName`}>
                  ຊື່ບໍລິສັດ/ບຸກຄົນ <span className="text-red-500">*</span>
                </Label>
                <Controller
                  control={control}
                  name={`shareholders.${index}.companyName`}
                  render={({ field: controllerField }) => {
                    const hasError =
                      shareholders[index]?.percentage > 0 &&
                      (!controllerField.value ||
                        controllerField.value.trim() === "");
                    return (
                      <Input
                        {...controllerField}
                        type="text"
                        placeholder="ປ້ອນຊື່ບໍລິສັດ..."
                        className={hasError ? "border-red-500" : ""}
                      />
                    );
                  }}
                />
              </div>
            </div>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <Button
            type="button"
            size="sm"
            variant="default"
            onClick={() =>
              append({
                id: Date.now().toString(),
                percentage: 0,
                companyName: "",
              })
            }
          >
            <Plus className="h-4 w-4 mr-1" />
            ເພີ່ມ
          </Button>
        </div>
      </div>
    </div>
  );
}

function GeneralInformation() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const companyId = String(localStorage.getItem("companyId")).replace(
    /['"]+/g,
    ""
  ); // Remove quotes from string

  // States
  const [selectedProvince, setSelectedProvince] = useState("");

  const [generalModal, setGeneralModal] = useState(false);
  const [shareholdingModal, setShareholdingModal] = useState(false);
  const [addressModal, setAddressModal] = useState(false);
  const [investModal, setInvestModal] = useState(false);
  const [personModal, setPersonModal] = useState(false);
  // Store domestic shareholders array for payload construction
  const [domesticShareholders, setDomesticShareholders] = useState<
    DomesticShareholder[]
  >([]);
  // Shareholding States
  const [shareholdingType, setShareholdingType] = useState<
    "govShareholding" | "domShareholding"
  >("govShareholding");
  const [shareholdingFields, setShareholdingFields] = useState<{
    govShareholding: number;
    domShareholding: number;
    govCompanyName: string;
    domCompanyName: string;
  }>({
    govShareholding: 0,
    domShareholding: 0,
    govCompanyName: "",
    domCompanyName: "",
  });
  // Form Data State
  const [selectedProvinceId, setSelectedProvinceId] = useState("");
  const [selectedDistrictId, setSelectedDistrictId] = useState("");

  // Separate state for initial data to prevent circular dependency
  const [initialDomesticShareholders, setInitialDomesticShareholders] =
    useState<DomesticShareholder[]>([]);

  // Validation function for shareholding form
  const validateShareholdingForm = useCallback(() => {
    if (shareholdingType === "govShareholding") {
      // For government enterprise: require government company selection
      return shareholdingFields.govCompanyName.trim() !== "";
    } else {
      // For mixed shareholding
      const hasGovShare = shareholdingFields.govShareholding > 0;
      const hasValidPrivateShares = domesticShareholders.some(
        (shareholder) =>
          shareholder.percentage > 0 && shareholder.companyName.trim() !== ""
      );

      // Check if any private shareholder has percentage but no company name
      const hasInvalidPrivateShares = domesticShareholders.some(
        (shareholder) =>
          shareholder.percentage > 0 && shareholder.companyName.trim() === ""
      );

      // Check if any private shareholder has company name but no percentage
      const hasInvalidPercentageShares = domesticShareholders.some(
        (shareholder) =>
          shareholder.percentage <= 0 && shareholder.companyName.trim() !== ""
      );

      // Must have at least government share OR private shares
      const hasAnyShares = hasGovShare || hasValidPrivateShares;

      // If has government share, must have company name
      const govShareValid =
        !hasGovShare || shareholdingFields.govCompanyName.trim() !== "";

      // Total percentage must be <= 100 and > 0
      const totalPercentage =
        shareholdingFields.govShareholding + shareholdingFields.domShareholding;
      const percentageValid = totalPercentage > 0 && totalPercentage <= 100;

      // No invalid private shares (percentage without company name OR company name without percentage)
      const privateSharesValid =
        !hasInvalidPrivateShares && !hasInvalidPercentageShares;

      // For mixed shareholding, must have at least one complete entry (either gov or private)
      const hasCompleteEntry =
        (hasGovShare && shareholdingFields.govCompanyName.trim() !== "") ||
        hasValidPrivateShares;

      return (
        hasAnyShares &&
        govShareValid &&
        percentageValid &&
        privateSharesValid &&
        hasCompleteEntry
      );
    }
  }, [shareholdingType, shareholdingFields, domesticShareholders]);

  // Optimized callback functions to prevent re-renders
  const handleGovShareChange = useCallback((value: number) => {
    setShareholdingFields((prev) => ({
      ...prev,
      govShareholding: value,
    }));
  }, []);

  const handleGovCompanyChange = useCallback((value: string) => {
    setShareholdingFields((prev) => ({
      ...prev,
      govCompanyName: value,
    }));
  }, []);

  const handleDomShareholdersChange = useCallback(
    (shareholders: DomesticShareholder[]) => {
      const totalDomShare = shareholders.reduce(
        (sum, sh) => sum + (Number(sh.percentage) || 0),
        0
      );
      const companyNames = shareholders
        .map((sh) => sh.companyName)
        .filter(Boolean)
        .join(", ");

      // Store the shareholders array for payload construction
      setDomesticShareholders(shareholders);

      setShareholdingFields((prev) => ({
        ...prev,
        domShareholding: totalDomShare,
        domCompanyName: companyNames,
      }));
    },
    []
  );

  const handleGovShareInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setShareholdingFields((prev) => ({
        ...prev,
        govShareholding: value === "" ? 0 : Number(value),
      }));
    },
    []
  );

  const { company_management } = useAccessPermission();

  // Queries
  const {
    data: SOEData,
    isLoading,
    error: SOEDataError,
  } = useQuery({
    queryKey: ["soe", companyId],
    queryFn: () => getSOE(companyId),
  });

  if (SOEDataError) {
    const isAxiosError = (
      error: unknown
    ): error is { response?: { data?: { message?: string } } } => {
      return typeof error === "object" && error !== null && "response" in error;
    };

    if (
      isAxiosError(SOEDataError) &&
      SOEDataError?.response?.data?.message === "LOGGED_OUT"
    ) {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  const [{ data: provincesRes }, { data: sectorsRes }] = useQueries({
    queries: [
      {
        queryKey: ["provinces"],
        queryFn: getProvinces,
      },
      {
        queryKey: ["sector"],
        queryFn: () => getSectors(),
      },
    ],
  });

  const { data: districtsRes } = useQuery({
    queryKey: ["district", selectedProvince],
    queryFn: () => getDistrict(selectedProvince),
    enabled: !!selectedProvince,
  });

  // Mutation
  const updateMutation = useMutation<unknown, unknown, unknown>({
    mutationFn: (formData) => updateSOE(companyId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["soe", companyId] });
      toast({
        title: "ສຳເລັດ",
        description: "ແກ້ໄຂບັນຊີສໍາເລັດແລ້ວ",
        variant: "success",
      });

      // Set All Modal To False
      setGeneralModal(false);
      setShareholdingModal(false);
      setAddressModal(false);
      setInvestModal(false);
      setPersonModal(false);
      // navigate({ to: "..", replace: true });
    },
    onError: (error) => {
      console.log(error);

      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
        variant: "destructive",
      });
    },
  });

  // Effect

  useEffect(() => {
    if (SOEData?.data) {
      setSelectedProvince(SOEData?.data?.province?.provinceCode);

      setSelectedProvinceId(SOEData?.data?.provinceId || "");
      setSelectedDistrictId(SOEData?.data?.districtId || "");
    }
  }, [SOEData?.data]);

  // Data
  const province = provincesRes?.data as Provinces[];
  const sector = sectorsRes?.data as CoreSector[];
  const district = districtsRes?.data as Districts[];

  if (isLoading) {
    return <Skeleton className="w-full h-[70vh] " />;
  }

  return (
    <section>
      {/* <p className="text-[14px] mb-[32px]">ຈັດການຂໍ້ມູນບໍລິສັດ</p> */}

      <Card className="border-none">
        <CardContent className="flex gap-x-5">
          <div className=" w-3/12 aspect-[4/3] flex items-start justify-center">
            <ImageUpload
              onImageUpload={(imageUrl) => {
                // Handle the uploaded image URL
                console.log(imageUrl);

                updateMutation.mutate({
                  companyProfileUrl: imageUrl,
                });
              }}
              showLabel={false}
              currentImage={SOEData?.data?.companyProfileUrl} // Pass existing image URL if any
              isSubmitting={updateMutation.isPending} // Control submit state if needed
            />
          </div>

          <div className="flex flex-col w-9/12 gap-y-5">
            {/* <p>{SOEData?.data?.companyName || "-"}</p> */}

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>ຂໍ້ມູນບໍລິສັດ</CardTitle>
                  {company_management.isUpdate && (
                    <Button
                      onClick={() => setGeneralModal(true)}
                      className="text-primary"
                      variant={"outline"}
                    >
                      <PenLine className="mr-2" /> ແກ້ໄຂຂໍ້ມູນ
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ເລກປະຈຳຕົວຜູ້ເສຍອາກອນ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.registrationId || "-"}
                    </span>
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ຊື່ບໍລິສັດ/ວິສາຫະກິດ
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.companyName || "-"}
                    </span>
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ຊື່ບໍລິສັດເປັນພາສາອັງກິດ
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.companyNameEng || "-"}
                    </span>
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ເລກທະບຽນວິສາຫະກິດ
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.taxpayerId || "-"}
                    </span>
                  </div>
                  {/* <div className="flex items-center w-full gap-3">
										<span className="font-medium text-gray-700 w-[30%]">
											ຂະແໜງການ:
										</span>
										<span className="w-[70%] font-bold">
											{SOEData?.data?.coreSector?.coreSector || "-"}
										</span>
									</div> */}
                  {/* <div className="flex items-center w-full gap-3">
										<span className="font-medium text-gray-700 w-[30%]">
											ຂະແໜງການ (ອາກອນ):
										</span>
										<span className="w-[70%] font-bold">
											{SOEData?.data?.sector?.sector || "-"}
										</span>
									</div> */}
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ທຸລະກິດຫຼັກ (ອາກອນ):
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.coreBusiness || "-"}
                    </span>
                  </div>

                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ປີສ້າງຕັ້ງ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.establishedDate
                        ? moment(SOEData?.data?.establishedDate).format(
                          "DD/MM/YYYY"
                        )
                        : "-"}
                    </span>{" "}
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ປະເພດວິສາຫະກິດ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.businessType || "-"}
                    </span>
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ຂັ້ນຄຸ້ມຄອງ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.managementLevel === "MINISTRY"
                        ? "ຂັ້ນສູນກາງ"
                        : "ຂັ້ນທ້ອງຖິ້ນ"}
                    </span>
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ເປົ້າໝາຍຍຸດທະສາດ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.economicRegion?.economicRegion || "-"}
                    </span>
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ຈຸດປະສົງ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.purpose || "-"}
                    </span>
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ຮາກຖານພັກຂຶ້ນກັບອົງຄະນະພັກ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.partyCommittee || "-"}
                    </span>
                  </div>
                  {/* <div className="flex items-center w-full gap-3">
                                        <span className="font-medium text-gray-700 w-[30%]">
                                            ການຖືບັນຊີ:
                                        </span>
                                        <span className="w-[70%] font-bold">
                                            {SOEData?.data?.accounting || "-"}
                                        </span>
                                    </div> */}
                </div>
              </CardContent>
            </Card>

            <Dialog open={generalModal} onOpenChange={setGeneralModal}>
              <DialogContent className="max-w-[800px]">
                <DialogHeader>
                  <DialogTitle>ຂໍ້ມູນບໍລິສັດ</DialogTitle>
                  <DialogDescription>ຟອມແກ້ໄຂຂໍ້ມູນບໍລິສັດ</DialogDescription>
                </DialogHeader>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target as HTMLFormElement);

                    const payload = {
                      registrationId: formData.get("registrationId"),
                      companyName: formData.get("companyName"),
                      companyNameEng: formData.get("companyNameEng"),
                      taxpayerId: formData.get("taxpayerId"),
                      // sectorId: formData.get('sectorId'),
                      // coreBusiness: formData.get('coreBusiness'),
                      establishedDate: formData.get("establishedDate"),
                      businessType: formData.get("businessType"),
                      // managementLevelId: formData.get('managementLevelId'),
                      purpose: formData.get("purpose"),
                      partyCommittee: formData.get("partyCommittee"),
                      // accounting:
                      //     formData.get("accounting"),
                    };

                    updateMutation.mutate(payload);
                  }}
                  className="grid gap-4 py-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="registrationId">
                        ເລກປະຈຳຕົວວິສາຫະກິດ
                      </Label>
                      <Input
                        id="registrationId"
                        name="registrationId"
                        defaultValue={SOEData?.data?.registrationId}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="taxpayerId">ເລກທະບຽນວິສາຫະກິດ</Label>
                      <Input
                        id="taxpayerId"
                        name="taxpayerId"
                        defaultValue={SOEData?.data?.taxpayerId}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="companyName">ຊື່ບໍລິສັດ/ວິສາຫະກິດ</Label>
                      <Input
                        id="companyName"
                        name="companyName"
                        defaultValue={SOEData?.data?.companyName}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="companyNameEng">
                        ຊື່ບໍລິສັດເປັນພາສາອັງກິດ
                      </Label>
                      <Input
                        id="companyNameEng"
                        name="companyNameEng"
                        defaultValue={SOEData?.data?.companyNameEng}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* <div>
											<Label htmlFor="sectorId">ຂະແໜງການ (ອາກອນ)</Label>
											<Select
												name="sectorId"
												defaultValue={SOEData?.data?.sectorId}
											>
												<SelectTrigger>
													<SelectValue placeholder="ເລືອກຂະແໜງການ" />
												</SelectTrigger>
												<SelectContent>
													{sector?.map((item) => (
														<SelectItem
															value={item.id}
															key={item.id}
														>
															{item?.sector}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div> */}
                    <div>
                      <Label htmlFor="coreBusiness">ທຸລະກິດຫຼັກ (ອາກອນ)</Label>
                      <Input
                        id="coreBusiness"
                        name="coreBusiness"
                        defaultValue={SOEData?.data?.coreBusiness}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="establishedDate">ປີສ້າງຕັ້ງ</Label>
                      <Input
                        id="establishedDate"
                        name="establishedDate"
                        type="date"
                        defaultValue={moment(
                          SOEData?.data?.establishedDate
                        ).format("YYYY-MM-DD")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="businessType">ປະເພດວິສາຫະກິດ</Label>
                      <Input
                        id="businessType"
                        name="businessType"
                        defaultValue={SOEData?.data?.businessType}
                      />
                    </div>
                  </div>

                  {/* <div>
										<Label htmlFor="managementLevelId">ຂັ້ນຄຸ້ມຄອງ</Label>
										<Select
											name="managementLevelId"
											defaultValue={SOEData?.data?.managementLevelId}
										>
											<SelectTrigger>
												<SelectValue placeholder="ເລືອກຂັ້ນຄຸ້ມຄອງ" />
											</SelectTrigger>
											<SelectContent>

											</SelectContent>
										</Select>
									</div> */}

                  <div>
                    <Label htmlFor="purpose">ຈຸດປະສົງ</Label>
                    <Input
                      id="purpose"
                      name="purpose"
                      defaultValue={SOEData?.data?.purpose}
                    />
                  </div>

                  <div>
                    <Label htmlFor="partyCommittee">
                      ຮາກຖານພັກຂຶ້ນກັບອົງຄະນະພັກ
                    </Label>
                    <Input
                      id="partyCommittee"
                      name="partyCommittee"
                      defaultValue={SOEData?.data?.partyCommittee}
                    />
                  </div>

                  {/* <div>
                                        <Label htmlFor="accounting">
                                            ການຖືບັນຊີ
                                        </Label>
                                        <Input
                                            id="accounting"
                                            name="accounting"
                                            defaultValue={
                                                SOEData?.data?.accounting
                                            }
                                        />
                                    </div> */}

                  <DialogFooter>
                    <DialogClose>
                      <Button variant={"outline"} type="button">
                        ຍົກເລີກ
                      </Button>
                    </DialogClose>
                    <Button type="submit">
                      {updateMutation.isPending ? (
                        <Spinner className="text-white" />
                      ) : (
                        <>
                          <Save className="mr-2" />
                          ບັນທຶກຂໍ້ມູນ
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* ພາກສ່ວນການຖືຮຸ້ນ */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>ພາກສ່ວນການຖືຮຸ້ນ</CardTitle>
                  {company_management.isUpdate && (
                    <Button
                      onClick={() => {
                        // Initialize form with backend data when modal opens
                        if (SOEData?.data?.shareholdings) {
                          const governmentShares =
                            SOEData.data.shareholdings.filter(
                              (s: ShareholdingData) =>
                                s.govShareholding !== null
                            );
                          const privateShares =
                            SOEData.data.shareholdings.filter(
                              (s: ShareholdingData) =>
                                s.privateShareholding !== null
                            );

                          const totalGovShareholding = governmentShares.reduce(
                            (sum: number, share: ShareholdingData) =>
                              sum + (share.govShareholding || 0),
                            0
                          );
                          const totalPrivateShareholding = privateShares.reduce(
                            (sum: number, share: ShareholdingData) =>
                              sum + (share.privateShareholding || 0),
                            0
                          );

                          // Determine shareholding type
                          const isGovEnterprise =
                            totalGovShareholding === 100 &&
                            privateShares.length === 0;
                          setShareholdingType(
                            isGovEnterprise
                              ? "govShareholding"
                              : "domShareholding"
                          );

                          // Set government company name (use the first government share)
                          const firstGovShare = governmentShares[0];

                          // Initialize shareholding fields
                          setShareholdingFields({
                            govShareholding: totalGovShareholding,
                            domShareholding: totalPrivateShareholding,
                            govCompanyName: firstGovShare?.govName || "",
                            domCompanyName: "", // Not used in current structure
                          });

                          // Initialize domestic shareholders array
                          const domesticShareholdersData = privateShares.map(
                            (share: ShareholdingData, index: number) => ({
                              companyName: share.privateName || "",
                              percentage: share.privateShareholding || 0,
                              id: share.id || `temp-${index}`,
                            })
                          );

                          setDomesticShareholders(domesticShareholdersData);
                          setInitialDomesticShareholders(
                            domesticShareholdersData
                          );
                        }

                        setShareholdingModal(true);
                      }}
                      className="text-primary"
                      variant={"outline"}
                    >
                      <PenLine className="mr-2" /> ແກ້ໄຂຂໍ້ມູນ
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {(() => {
                    // Calculate totals from shareholdings array
                    const governmentShares =
                      SOEData?.data?.shareholdings?.filter(
                        (s: ShareholdingData) => s.govShareholding !== null
                      ) || [];
                    const privateShares =
                      SOEData?.data?.shareholdings?.filter(
                        (s: ShareholdingData) => s.privateShareholding !== null
                      ) || [];

                    const totalGovShareholding = governmentShares.reduce(
                      (sum: number, share: ShareholdingData) =>
                        sum + (share.govShareholding || 0),
                      0
                    );
                    const totalPrivateShareholding = privateShares.reduce(
                      (sum: number, share: ShareholdingData) =>
                        sum + (share.privateShareholding || 0),
                      0
                    );

                    const isGovEnterprise = totalGovShareholding === 100;

                    return (
                      <>
                        <div className="flex items-center w-full gap-3">
                          <span className="font-medium text-gray-700 w-[30%]">
                            ປະເພດວິສາຫະກິດ:
                          </span>
                          <span className="w-[70%] font-bold">
                            {isGovEnterprise
                              ? "ລັດວິສາຫະກິດ"
                              : "ວິສະຫະກິດຖືຫຸ້ນ"}
                          </span>
                        </div>

                        {/* Government Shareholding Section */}
                        {governmentShares.length > 0 && (
                          <>
                            <div className="flex items-center w-full gap-3">
                              <span className="font-medium text-gray-700 w-[30%]">
                                ລັດຖືຮຸ້ນລວມ:
                              </span>
                              <span className="w-[70%] font-bold">
                                {totalGovShareholding} %
                              </span>
                            </div>
                            {governmentShares.map((share: ShareholdingData) => (
                              <div key={share.id} className="ml-4 space-y-1">
                                <div className="flex items-center w-full gap-3">
                                  <span className="font-medium text-gray-600 w-[30%] text-xs">
                                    • ລັດຖືຮຸ້ນ :
                                  </span>
                                  <span className="w-[70%] font-semibold">
                                    {share.govShareholding}%
                                  </span>
                                </div>
                                {share.govName && (
                                  <div className="flex items-center w-full gap-3">
                                    <span className="font-medium text-gray-600 w-[30%] text-xs">
                                      ກະຊວງ:
                                    </span>
                                    <span className="w-[70%] font-semibold">
                                      {share.govName}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </>
                        )}

                        {/* Private Shareholding Section */}
                        {privateShares.length > 0 && (
                          <>
                            <div className="flex items-center w-full gap-3">
                              <span className="font-medium text-gray-700 w-[30%]">
                                ເອກະຊົນຖືຮຸ້ນລວມ:
                              </span>
                              <span className="w-[70%] font-bold">
                                {totalPrivateShareholding} %
                              </span>
                            </div>
                            {privateShares.map(
                              (share: ShareholdingData, index: number) => (
                                <div key={share.id} className="ml-4 space-y-1">
                                  <div className="flex items-center w-full gap-3">
                                    <span className="font-medium text-gray-600 w-[30%] text-xs">
                                      • ເອກະຊົນຖືຮຸ້ນ {index + 1}:
                                    </span>
                                    <span className="w-[70%] font-semibold">
                                      {share.privateShareholding}%
                                    </span>
                                  </div>
                                  {share.privateName && (
                                    <div className="flex items-center w-full gap-3">
                                      <span className="font-medium text-gray-600 w-[30%] text-xs">
                                        ຊື່ບໍລິສັດ:
                                      </span>
                                      <span className="w-[70%] font-semibold">
                                        {share.privateName}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </>
                        )}

                        {/* Total Shareholding */}
                        <div className="border-t pt-2 mt-3">
                          <div className="flex items-center w-full gap-3">
                            <span className="font-medium text-gray-700 w-[30%]">
                              ຫຸ້ນລວມທັງໝົດ:
                            </span>
                            <span className="w-[70%] font-bold text-blue-600">
                              {totalGovShareholding + totalPrivateShareholding}{" "}
                              %
                            </span>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>

            <Dialog
              open={shareholdingModal}
              onOpenChange={setShareholdingModal}
            >
              <DialogContent className="max-w-[600px] overflow-y-auto max-h-[90vh]">
                <DialogHeader>
                  <DialogTitle>ພາກສ່ວນການຖືຮຸ້ນ</DialogTitle>
                  <DialogDescription>
                    ຟອມແກ້ໄຂຂໍ້ມູນພາກສ່ວນການຖືຮຸ້ນ
                  </DialogDescription>
                </DialogHeader>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();

                    const formData = new FormData(e.target as HTMLFormElement);

                    interface ShareholdingPayload {
                      isGovShareholding: boolean;
                      shareholdings: Array<{
                        govShareholding?: number;
                        govName?: string;
                        privateShareholding?: number;
                        privateName?: string;
                      }>;
                    }

                    let payload: ShareholdingPayload;

                    if (shareholdingType === "govShareholding") {
                      // ລັດວິສາຫະກິດ - 100% government only
                      payload = {
                        isGovShareholding: true,
                        shareholdings: [
                          {
                            govShareholding: 100,
                            govName:
                              (formData.get("govCompanyName") as string) || "",
                          },
                        ],
                      };
                    } else {
                      // ວິສະຫະກິດຖືຫຸ້ນ - Mixed shareholding
                      const govShare =
                        Number(formData.get("govShareholding")) || 0;
                      const govCompName =
                        (formData.get("govCompanyName") as string) || "";

                      const shareholdings = [];

                      // Add government shareholding if exists
                      if (govShare > 0) {
                        shareholdings.push({
                          govShareholding: govShare,
                          govName: govCompName,
                        });
                      }

                      // Add private shareholdings from dynamic field array
                      domesticShareholders.forEach((shareholder) => {
                        if (shareholder.percentage > 0) {
                          shareholdings.push({
                            privateShareholding: shareholder.percentage,
                            privateName: shareholder.companyName,
                          });
                        }
                      });

                      payload = {
                        isGovShareholding: false,
                        shareholdings: shareholdings,
                      };
                    }

                    updateMutation.mutate(
                      payload as unknown as Record<string, unknown>
                    );
                  }}
                  className="grid gap-4 py-4"
                >
                  <div>
                    <Label>
                      ປະເພດວິສາຫະກິດ <span className="text-red-500">*</span>
                    </Label>
                    <RadioGroup
                      name="shareholdingType"
                      value={shareholdingType}
                      className="flex items-center gap-[40px] mt-2"
                      onValueChange={(
                        value: "govShareholding" | "domShareholding"
                      ) => {
                        setShareholdingType(value);
                        if (value === "govShareholding") {
                          setShareholdingFields({
                            govShareholding: 100,
                            domShareholding: 0,
                            govCompanyName: "",
                            domCompanyName: "",
                          });
                        } else {
                          // Keep current values or use defaults without creating dependencies
                          setShareholdingFields((prev) => ({
                            govShareholding: prev.govShareholding || 0,
                            domShareholding: prev.domShareholding || 0,
                            govCompanyName: prev.govCompanyName || "",
                            domCompanyName: prev.domCompanyName || "",
                          }));
                        }
                      }}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="govShareholding"
                          id="govShareholding-radio"
                        />
                        <Label
                          htmlFor="govShareholding-radio"
                          className="cursor-pointer"
                        >
                          ລັດວິສາຫະກິດ
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="domShareholding"
                          id="domShareholding-radio"
                        />
                        <Label
                          htmlFor="domShareholding-radio"
                          className="cursor-pointer"
                        >
                          ວິສະຫະກິດຮຸ້ນສ່ວນ
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {shareholdingType === "govShareholding" ? (
                    <div className="space-y-4">
                      <div className="flex flex-col items-start gap-2 p-4 bg-gray-50 rounded-md">
                        <Label className="text-gray-700 font-medium">
                          ລັດຖືຫຸ້ນ 100%
                        </Label>
                      </div>
                      <div className="flex flex-col items-start gap-2">
                        <Label htmlFor="govCompanyName">
                          ກະຊວງ <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          name="govCompanyName"
                          value={shareholdingFields.govCompanyName}
                          onValueChange={handleGovCompanyChange}
                          required
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="ກະລຸນາເລືອກ..." />
                          </SelectTrigger>
                          <SelectContent>
                            {mockGovCompanies.map((company) => (
                              <SelectItem value={company.name} key={company.id}>
                                {company.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col items-start gap-2">
                          <Label htmlFor="govShareholding">
                            ລັດຖືຫຸ້ນ (%){" "}
                            <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="govShareholding"
                            name="govShareholding"
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={shareholdingFields.govShareholding || ""}
                            onChange={handleGovShareInputChange}
                            required
                          />
                        </div>
                        <div className="flex flex-col items-start gap-2">
                          <Label htmlFor="govCompanyName">
                            ຊື່ກະຊວງ{" "}
                            {shareholdingFields.govShareholding > 0 && (
                              <span className="text-red-500">*</span>
                            )}
                          </Label>
                          <Select
                            name="govCompanyName"
                            value={shareholdingFields.govCompanyName}
                            onValueChange={handleGovCompanyChange}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="ກະລຸນາເລືອກ..." />
                            </SelectTrigger>
                            <SelectContent>
                              {mockGovCompanies.map((company) => (
                                <SelectItem
                                  value={company.name}
                                  key={company.id}
                                >
                                  {company.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <DomesticShareholdersFieldArray
                        govShareholding={shareholdingFields.govShareholding}
                        onGovChange={handleGovShareChange}
                        govCompanyName={shareholdingFields.govCompanyName}
                        onGovCompanyChange={handleGovCompanyChange}
                        onDomShareholdersChange={handleDomShareholdersChange}
                        initialShareholders={initialDomesticShareholders}
                      />

                      {/* Hidden inputs for form submission */}
                      <input
                        type="hidden"
                        name="domShareholding"
                        value={shareholdingFields.domShareholding}
                      />
                      <input
                        type="hidden"
                        name="domCompanyName"
                        value={shareholdingFields.domCompanyName}
                      />

                      {/* Display total percentage */}
                      <div
                        className={`p-4 rounded-md border-2 ${shareholdingFields.govShareholding +
                            shareholdingFields.domShareholding >
                            100
                            ? "bg-red-50 border-red-300"
                            : shareholdingFields.govShareholding +
                              shareholdingFields.domShareholding ===
                              100
                              ? "bg-green-50 border-green-300"
                              : "bg-blue-50 border-blue-300"
                          }`}
                      >
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-700">ລັດຖືຮຸ້ນ:</span>
                            <span className="font-semibold text-blue-700">
                              {shareholdingFields.govShareholding}%
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-700">
                              ເອກະຊົນຖືຮຸ້ນລວມ:
                            </span>
                            <span className="font-semibold text-blue-700">
                              {shareholdingFields.domShareholding}%
                            </span>
                          </div>
                          <div className="border-t pt-2 mt-2">
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-gray-800">
                                ຜົນລວມທັງໝົດ:
                              </span>
                              <span
                                className={`font-bold text-lg ${shareholdingFields.govShareholding +
                                    shareholdingFields.domShareholding >
                                    100
                                    ? "text-red-600"
                                    : shareholdingFields.govShareholding +
                                      shareholdingFields.domShareholding ===
                                      100
                                      ? "text-green-600"
                                      : "text-blue-600"
                                  }`}
                              >
                                {shareholdingFields.govShareholding +
                                  shareholdingFields.domShareholding}
                                %
                              </span>
                            </div>
                            {shareholdingFields.govShareholding +
                              shareholdingFields.domShareholding >
                              100 && (
                                <p className="text-xs text-red-600 mt-1 text-right">
                                  ⚠️ ເກີນ 100%
                                </p>
                              )}
                            {shareholdingFields.govShareholding +
                              shareholdingFields.domShareholding ===
                              100 && (
                                <p className="text-xs text-green-600 mt-1 text-right">
                                  ✓ ຄົບ 100%
                                </p>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Validation Error Messages */}
                  {!validateShareholdingForm() && (
                    <div className="p-3 rounded-md bg-red-50 border border-red-200">
                      <p className="text-sm text-red-600 font-medium">
                        ກະລຸນາກວດສອບຂໍ້ມູນດັ່ງນີ້:
                      </p>
                      <ul className="list-disc list-inside text-sm text-red-600 mt-1 space-y-1">
                        {shareholdingType === "govShareholding" &&
                          shareholdingFields.govCompanyName.trim() === "" && (
                            <li>ກະລຸນາເລືອກກະຊວງ</li>
                          )}
                        {shareholdingType === "domShareholding" && (
                          <>
                            {shareholdingFields.govShareholding +
                              shareholdingFields.domShareholding ===
                              0 && <li>ກະລຸນາປ້ອນການຖືຫຸ້ນຢ່າງໜ້ອຍ 1 ປະເພດ</li>}
                            {shareholdingFields.govShareholding > 0 &&
                              shareholdingFields.govCompanyName.trim() ===
                              "" && (
                                <li>
                                  ກະລຸນາເລືອກຊື່ກະຊວງ ເມື່ອມີການຖືຫຸ້ນຂອງລັດ
                                </li>
                              )}
                            {shareholdingFields.govShareholding +
                              shareholdingFields.domShareholding >
                              100 && <li>ຜົນລວມການຖືຫຸ້ນຕ້ອງບໍ່ເກີນ 100%</li>}
                            {domesticShareholders.some(
                              (sh) =>
                                sh.percentage > 0 &&
                                sh.companyName.trim() === ""
                            ) && (
                                <li>
                                  ກະລຸນາປ້ອນຊື່ບໍລິສັດສຳລັບທຸກການຖືຫຸ້ນເອກະຊົນທີ່ມີເປີເຊັນ
                                </li>
                              )}
                            {domesticShareholders.some(
                              (sh) =>
                                sh.percentage <= 0 &&
                                sh.companyName.trim() !== ""
                            ) && (
                                <li>
                                  ກະລຸນາປ້ອນເປີເຊັນສຳລັບທຸກບໍລິສັດເອກະຊົນທີ່ມີຊື່
                                </li>
                              )}
                            {!domesticShareholders.some(
                              (sh) =>
                                sh.percentage > 0 &&
                                sh.companyName.trim() !== ""
                            ) &&
                              shareholdingFields.govShareholding <= 0 && (
                                <li>
                                  ກະລຸນາປ້ອນຂໍ້ມູນການຖືຫຸ້ນທີ່ສົມບູນຢ່າງໜ້ອຍ 1
                                  ລາຍການ (ທັງເປີເຊັນ ແລະ ຊື່ບໍລິສັດ)
                                </li>
                              )}
                          </>
                        )}
                      </ul>
                    </div>
                  )}

                  <DialogFooter>
                    <DialogClose>
                      <Button variant={"outline"} type="button">
                        ຍົກເລີກ
                      </Button>
                    </DialogClose>
                    <Button
                      type="submit"
                      disabled={
                        updateMutation.isPending ||
                        shareholdingFields.govShareholding +
                        shareholdingFields.domShareholding >
                        100 ||
                        !validateShareholdingForm()
                      }
                    >
                      {updateMutation.isPending ? (
                        <Spinner className="text-white" />
                      ) : (
                        <>
                          <Save className="mr-2" />
                          ບັນທຶກຂໍ້ມູນ
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* ທີ່ຕັ້ງສຳນັກງານໃຫ່ຍຂອງວິສາຫະກິດ */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>ທີ່ຕັ້ງສຳນັກງານໃຫຍ່ຂອງວິສາຫະກິດ</CardTitle>
                  {company_management.isUpdate && (
                    <Button
                      onClick={() => setAddressModal(true)}
                      className="text-primary"
                      variant={"outline"}
                    >
                      <PenLine className="mr-2" /> ແກ້ໄຂຂໍ້ມູນ
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ແຂວງ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.province?.province || "-"}
                    </span>{" "}
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ເມືອງ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.district?.district || "-"}
                    </span>{" "}
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ບ້ານ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.village || "-"}
                    </span>{" "}
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ເບີໂທລະສັບ / Fax:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.phone || "-"}
                    </span>{" "}
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      Email:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.email || "-"}
                    </span>{" "}
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      Website:
                    </span>
                    {SOEData?.data?.websiteUrl ? (
                      <a
                        href={SOEData.data.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-blue-500 max-w-[70%] font-bold break-words"
                      >
                        {SOEData.data.websiteUrl}
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Dialog open={addressModal} onOpenChange={setAddressModal}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>ທີ່ຕັ້ງສຳນັກງານໃຫຍ່ຂອງວິສາຫະກິດ</DialogTitle>
                  <DialogDescription>
                    ຟອມແກ້ໄຂຂໍ້ມູນທີ່ຕັ້ງສຳນັກງານໃຫຍ່ຂອງວິສາຫະກິດ
                  </DialogDescription>
                </DialogHeader>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();

                    const formData = new FormData(e.target as HTMLFormElement);

                    const payload = {
                      provinceId: selectedProvinceId,
                      districtId: selectedDistrictId,
                      village: formData.get("village"),
                      phone: formData.get("phone"),
                      email: formData.get("email"),
                      websiteUrl: formData.get("websiteUrl"),
                    };

                    console.log(payload, "Payload");

                    updateMutation.mutate(payload);
                  }}
                  className="grid gap-4 py-4"
                >
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="provinceId">ແຂວງ</Label>
                      <Select
                        name="provinceId"
                        onValueChange={(value) => {
                          setSelectedProvinceId(value);
                          const selectedProvinceData = province?.find(
                            (province) => province.id === value
                          );
                          if (selectedProvinceData) {
                            setSelectedProvince(
                              selectedProvinceData.provinceCode
                            );
                          }
                        }}
                        defaultValue={SOEData?.data?.provinceId}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="ເລືອກແຂວງ" />
                        </SelectTrigger>
                        <SelectContent>
                          {province?.map((province) => (
                            <SelectItem
                              value={province.id}
                              key={province.provinceCode}
                            >
                              {province?.province}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="districtId">ເມືອງ</Label>
                      <Select
                        name="districtId"
                        onValueChange={(value) => setSelectedDistrictId(value)}
                        disabled={!selectedProvince}
                        defaultValue={SOEData?.data?.districtId}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="ເລືອກເມືອງ" />
                        </SelectTrigger>
                        <SelectContent>
                          {district?.map((district) => (
                            <SelectItem
                              value={district.id}
                              key={district.districtCode}
                            >
                              {district?.district}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="village">ບ້ານ</Label>

                      <Input
                        id="village"
                        type="text"
                        placeholder="ປ້ອນຊື່ບ້ານ..."
                        className="w-full"
                        name="village"
                        defaultValue={SOEData?.data?.village}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">ເບີໂທ</Label>

                      <Input
                        id="phone"
                        type="text"
                        placeholder="ປ້ອນຊື່ເບີໂທ..."
                        className="w-full"
                        name="phone"
                        defaultValue={SOEData?.data?.phone}
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">ອີເມວ</Label>

                      <Input
                        id="email"
                        type="email"
                        placeholder="ປ້ອນຊື່ອີເມວ..."
                        className="w-full"
                        name="email"
                        defaultValue={SOEData?.data?.email}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="websiteUrl">ລິ້ງເວັບໄຊທ໌</Label>

                    <Input
                      id="websiteUrl"
                      type="text"
                      placeholder="ປ້ອນຊື່ລິ້ງເວັບໄຊທ໌..."
                      className="w-full"
                      name="websiteUrl"
                      defaultValue={SOEData?.data?.websiteUrl}
                    />
                  </div>

                  <DialogFooter>
                    <DialogClose>
                      <Button variant={"outline"} type="button">
                        ຍົກເລີກ
                      </Button>
                    </DialogClose>
                    <Button type="submit">
                      {updateMutation.isPending ? (
                        <Spinner className="text-white" />
                      ) : (
                        <>
                          <Save className="mr-2" />
                          ບັນທຶກຂໍ້ມູນ
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* ການລົງທຶນ */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>ການລົງທຶນ</CardTitle>
                  {company_management.isUpdate && (
                    <Button
                      onClick={() => setInvestModal(true)}
                      className="text-primary"
                      variant={"outline"}
                    >
                      <PenLine className="mr-2" /> ແກ້ໄຂຂໍ້ມູນ
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ທຶນທັງໝົດ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.totalFund?.toLocaleString() || "-"}
                    </span>{" "}
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ທຶນຈົດທະບຽນ (ກີບ):
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.registeredCapital?.toLocaleString() ||
                        "-"}
                    </span>{" "}
                  </div>

                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ຖອກແລ້ວ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.paidCapital?.toLocaleString() || "-"}
                    </span>{" "}
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ຍັງບໍ່ຖອກ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.unpaidCapital?.toLocaleString() || "-"}
                    </span>{" "}
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ໃບອະນຸຍາດລົງທຶນເລກທີ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.permitNo || "-"}
                    </span>{" "}
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ອອກໃຫ້ວັນທີ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.permitAt || "-"}
                    </span>{" "}
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ໃບສຳປະທານເລກທີ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.concessionNo || "-"}
                    </span>{" "}
                  </div>
                  <div className="flex items-center w-full gap-3">
                    <span className="font-medium text-gray-700 w-[30%]">
                      ອອກໃຫ້ວັນທີ:
                    </span>
                    <span className="w-[70%] font-bold">
                      {SOEData?.data?.concessionAt || "-"}
                    </span>{" "}
                  </div>
                  {/* <div className="flex items-center w-full gap-3">
										<span className="font-medium text-gray-700 w-[30%]">
											ຂະແໜງການ:
										</span>
										<span className="w-[70%] font-bold">
											{SOEData?.data?.coreSector?.coreSector || "-"}
										</span>{" "}
									</div> */}
                </div>
              </CardContent>
            </Card>

            <Dialog open={investModal} onOpenChange={setInvestModal}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>ການລົງທຶນ</DialogTitle>
                  <DialogDescription>ຟອມແກ້ໄຂຂໍ້ມູນການລົງທຶນ</DialogDescription>
                </DialogHeader>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target as HTMLFormElement);

                    // Debug: Log all form data
                    console.log("Form data entries:");
                    for (const [key, value] of formData.entries()) {
                      console.log(key, value);
                    }

                    const payload = {
                      totalFund: Number(formData.get("totalFund")) || 0,
                      registeredCapital:
                        Number(formData.get("registeredCapital")) || 0,
                      paidCapital: Number(formData.get("paidCapital")) || 0,
                      unpaidCapital: Number(formData.get("unpaidCapital")) || 0,
                      permitNo: formData.get("permitNo"),
                      permitAt: formData.get("permitAt"),
                      concessionNo: formData.get("concessionNo"),
                      concessionAt: formData.get("concessionAt"),
                      coreSectorId: formData.get("coreSectorId"),
                    };

                    console.log("Payload:", payload); // Debug log
                    updateMutation.mutate(payload);
                  }}
                  className="grid gap-4 py-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <FormattedNumberInput
                      id="totalFund"
                      name="totalFund" // This should match the name used in formData.get()
                      label="ທຶນທັງໝົດ"
                      defaultValue={SOEData?.data?.totalFund}
                      required={true}
                      placeholder="0.00"
                    />
                    <FormattedNumberInput
                      id="registeredCapital"
                      name="registeredCapital" // This should match the name used in formData.get()
                      label="ທຶນຈົດທະບຽນ (ກີບ)"
                      defaultValue={SOEData?.data?.registeredCapital}
                      required={true}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormattedNumberInput
                      id="paidCapital"
                      name="paidCapital" // This should match the name used in formData.get()
                      label="ຖອກແລ້ວ"
                      defaultValue={SOEData?.data?.paidCapital}
                      required={true}
                      placeholder="0.00"
                    />
                    <FormattedNumberInput
                      id="unpaidCapital"
                      name="unpaidCapital" // This should match the name used in formData.get()
                      label="ຍັງບໍ່ຖອກ"
                      defaultValue={SOEData?.data?.unpaidCapital}
                      required={true}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="permitNo">ໃບອະນຸຍາດລົງທຶນເລກທີ</Label>
                      <Input
                        id="permitNo"
                        name="permitNo"
                        type="text"
                        defaultValue={SOEData?.data?.permitNo}
                      />
                    </div>
                    <div>
                      <Label htmlFor="permitAt">ອອກໃຫ້ວັນທີ</Label>
                      <Input
                        id="permitAt"
                        name="permitAt"
                        type="date"
                        defaultValue={SOEData?.data?.permitAt}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="concessionNo">ໃບສຳປະທານເລກທີ</Label>
                      <Input
                        id="concessionNo"
                        name="concessionNo"
                        type="text"
                        defaultValue={SOEData?.data?.concessionNo}
                      />
                    </div>
                    <div>
                      <Label htmlFor="concessionAt">ອອກໃຫ້ວັນທີ</Label>
                      <Input
                        id="concessionAt"
                        name="concessionAt"
                        type="date"
                        defaultValue={SOEData?.data?.concessionAt}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="coreSectorId">
                      ຂະແໜງການ <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      name="coreSectorId"
                      defaultValue={SOEData?.data?.coreSectorId}
                      required
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="ເລືອກຂະແໜງການ" />
                      </SelectTrigger>
                      <SelectContent>
                        {sector?.map((item) => (
                          <SelectItem value={item.id} key={item.id}>
                            {item?.coreSector}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <DialogFooter>
                    <DialogClose>
                      <Button variant={"outline"} type="button">
                        ຍົກເລີກ
                      </Button>
                    </DialogClose>
                    <Button type="submit">
                      {updateMutation.isPending ? (
                        <Spinner className="text-white" />
                      ) : (
                        <>
                          <Save className="mr-2" />
                          ບັນທຶກຂໍ້ມູນ
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* ຂໍ້ມູນພະນັກງານ */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>ຂໍ້ມູນພະນັກງານ</CardTitle>
                  {company_management.isUpdate && (
                    <Button
                      onClick={() => setPersonModal(true)}
                      className="text-primary"
                      variant={"outline"}
                    >
                      <PenLine className="mr-2" /> ແກ້ໄຂຂໍ້ມູນ
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 text-sm">
                  <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 w-[50%]">
                        ສະພາບໍລິຫານ:
                      </span>
                      <span className="font-bold">
                        {SOEData?.data?.boardCount || "-"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 w-[50%]">
                        ຍິງ:
                      </span>
                      <span className="font-bold">
                        {SOEData?.data?.femaleBoardCount || "-"}
                      </span>
                    </div>
                  </div>

                  {/* Directors Row */}
                  <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 w-[50%]">
                        ຄະນະອຳນວຍການ:
                      </span>
                      <span className="font-bold">
                        {SOEData?.data?.directorCount || "-"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 w-[50%]">
                        ຍິງ:
                      </span>
                      <span className="font-bold">
                        {SOEData?.data?.femaleDirectorCount || "-"}
                      </span>
                    </div>
                  </div>

                  {/* Employees Row */}
                  <div className="grid grid-cols-2 gap-8">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 w-[50%]">
                        ພະນັກງານທັງໝົດ:
                      </span>
                      <span className="font-bold">
                        {SOEData?.data?.empCount || "-"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 w-[50%]">
                        ຍິງ:
                      </span>
                      <span className="font-bold">
                        {SOEData?.data?.femaleEmpCount || "-"}
                      </span>
                    </div>
                  </div>

                  {/* Party Members Row */}
                  <div className="grid grid-cols-2 gap-8">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 w-[50%]">
                        ສະມາຊິກພັກ:
                      </span>
                      <span className="font-bold">
                        {SOEData?.data?.partyCount || "-"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 w-[50%]">
                        ຍິງ:
                      </span>
                      <span className="font-bold">
                        {SOEData?.data?.femalePartyCount || "-"}
                      </span>
                    </div>
                  </div>

                  {/* Union Members Row */}
                  <div className="grid grid-cols-2 gap-8">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 w-[50%]">
                        ສະມາຊິກກຳມະບານ:
                      </span>
                      <span className="font-bold">
                        {SOEData?.data?.unionCount || "-"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 w-[50%]">
                        ຍິງ:
                      </span>
                      <span className="font-bold">
                        {SOEData?.data?.femaleUnionCount || "-"}
                      </span>
                    </div>
                  </div>

                  {/* Women Members Row */}
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700 w-[25%]">
                      ສະມາຊິກແມ່ຍິງ:
                    </span>
                    <span className="font-bold">
                      {SOEData?.data?.womenCount || "-"}
                    </span>
                  </div>

                  {/* Youth Members Row */}
                  <div className="grid grid-cols-2 gap-8">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 w-[50%]">
                        ສະມາຊິກຊາວໜຸ່ມ:
                      </span>
                      <span className="font-bold">
                        {SOEData?.data?.youthCount || "-"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-700 w-[50%]">
                        ຍິງ:
                      </span>
                      <span className="font-bold">
                        {SOEData?.data?.femaleYouthCount || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Dialog open={personModal} onOpenChange={setPersonModal}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>ພະນັກງານ</DialogTitle>
                  <DialogDescription>ຟອມແກ້ໄຂຂໍ້ມູນພະນັກງານ</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    variant={"outline"}
                    onClick={() =>
                      navigate({
                        to: "/account-management/soe/director",
                        search: {
                          page: 1,
                          activeStatus: undefined,
                        },
                      })
                    }
                  >
                    <Users className="w-4 h-4 mr-2" />
                    ຈັດການຂໍ້ມູນຄະນະອຳນວຍການ
                  </Button>

                  <Button
                    variant={"outline"}
                    onClick={() =>
                      navigate({
                        to: "/account-management/soe/board-of-directors",
                        search: {
                          page: 1,
                          activeStatus: undefined,
                        },
                      })
                    }
                  >
                    <Users className="w-4 h-4 mr-2" />
                    ຈັດການຂໍ້ມູນສະພາບໍລິຫານ
                  </Button>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target as HTMLFormElement);

                    const payload = {
                      partyCount: Number(formData.get("partyCount")),
                      femalePartyCount: Number(
                        formData.get("femalePartyCount")
                      ),
                      unionCount: Number(formData.get("unionCount")),
                      femaleUnionCount: Number(
                        formData.get("femaleUnionCount")
                      ),
                      womenCount: Number(formData.get("womenCount")),
                      youthCount: Number(formData.get("youthCount")),
                      femaleYouthCount: Number(
                        formData.get("femaleYouthCount")
                      ),
                    };

                    updateMutation.mutate(payload);
                  }}
                  className="grid gap-4 py-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="partyCount">ສະມາຊິກພັກ</Label>
                      <Input
                        id="partyCount"
                        name="partyCount"
                        type="number"
                        defaultValue={SOEData?.data?.partyCount}
                      />
                    </div>
                    <div>
                      <Label htmlFor="femalePartyCount">ຍິງ</Label>
                      <Input
                        id="femalePartyCount"
                        name="femalePartyCount"
                        type="number"
                        defaultValue={SOEData?.data?.femalePartyCount}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="unionCount">ສະມາຊິກກຳມະບານ</Label>
                      <Input
                        id="unionCount"
                        name="unionCount"
                        type="number"
                        defaultValue={SOEData?.data?.unionCount}
                      />
                    </div>
                    <div>
                      <Label htmlFor="femaleUnionCount">ຍິງ</Label>
                      <Input
                        id="femaleUnionCount"
                        name="femaleUnionCount"
                        type="number"
                        defaultValue={SOEData?.data?.femaleUnionCount}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="womenCount">ສະມາຊິກແມ່ຍິງ</Label>
                    <Input
                      id="womenCount"
                      name="womenCount"
                      type="number"
                      defaultValue={SOEData?.data?.womenCount}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="youthCount">ສະມາຊິກຊາວໜຸ່ມ</Label>
                      <Input
                        id="youthCount"
                        name="youthCount"
                        type="number"
                        defaultValue={SOEData?.data?.youthCount}
                      />
                    </div>
                    <div>
                      <Label htmlFor="femaleYouthCount">ຍິງ</Label>
                      <Input
                        id="femaleYouthCount"
                        name="femaleYouthCount"
                        type="number"
                        defaultValue={SOEData?.data?.femaleYouthCount}
                      />
                    </div>
                  </div>

                  <DialogFooter>
                    <DialogClose>
                      <Button variant={"outline"} type="button">
                        ຍົກເລີກ
                      </Button>
                    </DialogClose>
                    <Button type="submit">
                      {updateMutation.isPending ? (
                        <Spinner className="text-white" />
                      ) : (
                        <>
                          <Save className="mr-2" />
                          ບັນທຶກຂໍ້ມູນ
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* ຂໍ້ມູນການອັບເດດວັນທີ */}
            <Card>
              <CardHeader>
                <CardTitle>ຂໍ້ມູນການອັບເດດບັນຊີ</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center w-full gap-3">
                      <span className="font-medium text-gray-700 w-[30%]">
                        {" "}
                        ສ້າງໂດຍ
                      </span>
                      <span className="w-[70%] font-bold">
                        {SOEData?.data?.createdBy || "-"}
                      </span>
                    </div>
                    <div className="flex items-center w-full gap-3">
                      <span className="font-medium text-gray-700 w-[30%]">
                        {" "}
                        ວັນທີສ້າງ
                      </span>
                      <span className="w-[70%] font-bold">
                        {SOEData?.data?.createdAt
                          ? moment(SOEData?.data?.createdAt).format(
                            "DD/MM/YYYY"
                          )
                          : "-"}
                      </span>
                    </div>
                    <div className="flex items-center w-full gap-3">
                      <span className="font-medium text-gray-700 w-[30%]">
                        {" "}
                        ອັບເດດໂດຍ
                      </span>
                      <span className="w-[70%] font-bold">
                        {SOEData?.data?.updatedBy || "-"}
                      </span>
                    </div>
                    <div className="flex items-center w-full gap-3">
                      <span className="font-medium text-gray-700 w-[30%]">
                        {" "}
                        ອັບເດດລ່າສຸດ
                      </span>
                      <span className="w-[70%] font-bold">
                        {SOEData?.data?.updatedAt
                          ? moment(SOEData?.data?.updatedAt).format(
                            "DD/MM/YYYY"
                          )
                          : "-"}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
